import { Machine, MachineOptions, EventObject, assign } from 'xstate'
import { AnyUserData } from 'types'

export interface usersMachineContext {
  users: { [uid: string]: AnyUserData }
  lastUserEvent: { type: string; data: any }
}

export interface usersMachineStates {
  states: {
    ready: {}
    updatingUser: {}
    creatingUser: {}
    readingUser: {}
    loadingUsers: {}
  }
}

export interface usersMachineEvents extends EventObject {
  types: 'CREATE' | 'UDPATE' | 'READ' | 'LOAD_USERS'
  data: {
    user: Partial<AnyUserData>
    users: Partial<AnyUserData>[]
    eventId: string
  }
}

const userMachineOptions: Partial<
  MachineOptions<usersMachineContext, usersMachineEvents>
> = {
  actions: {
    makeCurrentEvent: assign((ctx, evt) => ({
      lastUserEvent: {
        type: evt.type,
        data: evt.data.user || evt.data.users,
      },
    })),
    updateUser: assign((ctx, evt) => {
      const { id, ...updatedData } = evt.data.user
      if (!id) throw Error('NO ID ON UPDATED USER!')
      const localUser = ctx.users[id] ?? {}
      const updatedUser = {
        ...localUser,
        ...updatedData,
        id,
      }
      return {
        users: {
          ...ctx.users,
          [id]: updatedUser,
        },
      }
    }),
    createUser: assign((ctx: any, evt: any) => {
      const user = evt.data.user
      return {
        users: {
          ...ctx.users,
          [user.id]: user,
        },
      }
    }),
    readUser: assign((ctx: any, evt: any) => {
      const user = evt.data.user
      return {
        users: {
          ...ctx.users,
          [user.id]: user,
        },
      }
    }),

    loadUsers: assign((ctx: any, evt: any) => {
      const users = { ...ctx.users }
      evt.data.users.forEach((user: AnyUserData) => {
        users[user.id] = { ...users[user.id], ...user }
      })
      return {
        users,
      }
    }),
  },
}

export const usersMachine = Machine<
  usersMachineContext,
  usersMachineStates,
  usersMachineEvents
>(
  {
    id: 'usersMachine',
    initial: 'ready',
    context: {
      users: {},
      lastUserEvent: {
        type: '',
        data: {},
      },
    },
    states: {
      ready: {},
      updatingUser: {
        after: {
          100: {
            target: 'ready',
          },
        },
      },
      creatingUser: {
        after: {
          100: {
            target: 'ready',
          },
        },
      },
      readingUser: {
        after: {
          100: {
            target: 'ready',
          },
        },
      },
      loadingUsers: {
        after: {
          100: {
            target: 'ready',
          },
        },
      },
    },
    on: {
      UPDATE: {
        target: 'updatingUser',
        actions: ['makeCurrentEvent', 'updateUser'],
      },
      READ: {
        target: 'readingUser',
        actions: ['makeCurrentEvent', 'readUser'],
      },
      CREATE: {
        target: 'creatingUser',
        actions: ['makeCurrentEvent', 'createUser'],
      },
      LOAD_USERS: {
        target: 'loadingUsers',
        actions: ['makeCurrentEvent', 'loadUsers'],
      },
    },
  },
  userMachineOptions
)
